import { Box, Typography } from '@mui/material';
import React from 'react';
import {
    ADMIN_JOB_CV_REJECTION_REASONS,
    AdminJobCvRejectionReason,
} from '../../../common/admin_job_cv_rejection_reason.enum';
import {
    AdminAiMatchRejectionResponse, createAdminAiMatchRejection, removeAdminAiMatchRejection,
} from '../../api/adminAiMatchRejections.api';
import Button from '../foundation/Button';
import Select from '../foundation/Select/Select';

export const ADMIN_REJECTION_REASON_LABELS: Record<AdminJobCvRejectionReason, string> = {
    [ADMIN_JOB_CV_REJECTION_REASONS.GENERAL_MISMATCH]: 'אי־התאמה כללית',
    [ADMIN_JOB_CV_REJECTION_REASONS.ROLE_DOMAIN_MISMATCH]: 'תפקיד או תחום לא מתאימים',
    [ADMIN_JOB_CV_REJECTION_REASONS.EXPERIENCE_MISMATCH]: 'ניסיון לא מתאים',
    [ADMIN_JOB_CV_REJECTION_REASONS.SKILLS_MISMATCH]: 'כישורים לא מתאימים',
    [ADMIN_JOB_CV_REJECTION_REASONS.EDUCATION_MISMATCH]: 'השכלה לא מתאימה',
    [ADMIN_JOB_CV_REJECTION_REASONS.LANGUAGE_MISMATCH]: 'שפות לא מתאימות',
    [ADMIN_JOB_CV_REJECTION_REASONS.LOCATION_MISMATCH]: 'מיקום לא מתאים',
    [ADMIN_JOB_CV_REJECTION_REASONS.WORK_CONDITIONS_MISMATCH]: 'תנאי עבודה לא מתאימים',
    [ADMIN_JOB_CV_REJECTION_REASONS.INSUFFICIENT_DATA]: 'אין מספיק מידע',
};

const REJECTION_REASON_OPTIONS: Array<{
    text: string;
    value: AdminJobCvRejectionReason;
}> = Object.values(ADMIN_JOB_CV_REJECTION_REASONS).map(value => ({
    text: ADMIN_REJECTION_REASON_LABELS[value],
    value,
}));

type AdminAiMatchRejectionControlProps = {
    jobId: number;
    cvId: number;
    isRejected: boolean;
    onRejected: (rejection: AdminAiMatchRejectionResponse) => void;
    onRejectionRemoved: () => void;
};

const AdminAiMatchRejectionControl = ({
    jobId,
    cvId,
    isRejected,
    onRejected,
    onRejectionRemoved,
}: AdminAiMatchRejectionControlProps): JSX.Element => {
    const [reasonCode, setReasonCode] = React.useState<AdminJobCvRejectionReason>(
        ADMIN_JOB_CV_REJECTION_REASONS.GENERAL_MISMATCH,
    );
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const rejectMatch = async () => {
        setSubmitting(true);
        setError('');
        try {
            const response = await createAdminAiMatchRejection({ jobId, cvId, reasonCode });
            onRejected(response.data);
        } catch (err: any) {
            const responseData = err?.response?.data;
            const message = (typeof responseData === 'string' && responseData)
                || responseData?.message
                || 'שמירת השלילה נכשלה. אפשר לנסות שוב.';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const removeRejection = async () => {
        setSubmitting(true);
        setError('');
        try {
            await removeAdminAiMatchRejection({ jobId, cvId });
            onRejectionRemoved();
        } catch (err: any) {
            const responseData = err?.response?.data;
            const message = (typeof responseData === 'string' && responseData)
                || responseData?.message
                || 'ביטול השלילה נכשל. אפשר לנסות שוב.';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box style={{ flexShrink: 0 }}>
            <Box style={{
                display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
            }}>
                {isRejected ? (
                    <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={submitting}
                        onClick={removeRejection}
                    >
                        {submitting ? 'מבטל...' : 'ביטול שלילה'}
                    </Button>
                ) : (
                    <>
                        <Select
                            size="small"
                            value={reasonCode}
                            disabled={submitting}
                            inputProps={{ 'aria-label': 'סיבת שלילת ההתאמה' }}
                            options={REJECTION_REASON_OPTIONS}
                            onChange={event => setReasonCode(
                                event.target.value as AdminJobCvRejectionReason,
                            )}
                        />
                        <Button
                            size="small"
                            color="error"
                            disabled={submitting}
                            onClick={rejectMatch}
                        >
                            {submitting ? 'שומר...' : 'שלילה'}
                        </Button>
                    </>
                )}
            </Box>
            {error && (
                <Typography variant="caption" color="error" display="block">
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default AdminAiMatchRejectionControl;
